import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import React, { useMemo } from 'react';
import { HesselSite } from '../../../components/site/hessel-site.component';
import { ErrorHandlingProvider } from '../../../error-handling';
import { useCookiebot } from '../../../hooks/use-cookiebot';
import { useUrlHandler } from '../../../hooks/use-url-handler';
import { umbraco } from '../../../lib/api';
import { hesselViewModels } from '../../../lib/view-models';
import { getInstrumentationKey } from '../../../utils/application-insights.helpers';
import {
    familyAndConfigurationsBuilder,
    getCarSsrQueryColor,
    getCheapestCarSsrColor,
    getPdpMetaDescription,
    getPdpPageTitle,
    getProtocol,
    handleDiscontinuedVehicles,
} from '../../../utils/helpers';

type Props = {
    page: umbraco.Page;
    siteSettings: umbraco.SiteSettings;
    carFamily: Array<hesselViewModels.ProductDetails>;
    initialProductData: {
        product: hesselViewModels.ProductDetails;
        color: hesselViewModels.ProductColor;
        queryColor: hesselViewModels.ProductColor;
        queryPurchaseType?: hesselViewModels.OwnershipTab;
        isCompanyCar: boolean | null;
    };
    allConfigurations: hesselViewModels.CarConfiguration;
};

const getSiteComponents = (props: Props) => {
    switch (props.page.contentTypeAlias) {
        case 'productDetailsPage':
            return (
                <HesselSite
                    page={{
                        ...props.page,
                        metaTitle: props.initialProductData.product
                            ? getPdpPageTitle(
                                  props.initialProductData.product,
                                  props.initialProductData.color?.name ?? props.initialProductData.product?.colors[0].name
                              )
                            : props.page.metaTitle,
                        metaDescription: props.initialProductData.product
                            ? getPdpMetaDescription(
                                  props.initialProductData.product,
                                  props.initialProductData.color?.name ?? props.initialProductData.product?.colors[0].name
                              )
                            : props.page.metaDescription,
                    }}
                    siteSettings={props.siteSettings}
                    pdpData={{
                        allConfigurations: props.allConfigurations,
                        carFamily: props.carFamily,
                        product: props.initialProductData.product,
                        color: props.initialProductData.color,
                        queryColor: props.initialProductData.queryColor,
                        queryPurchaseType: props.initialProductData.queryPurchaseType || null,
                        isCompanyCar: props.initialProductData.isCompanyCar,
                    }}
                />
            );
        default:
            return <h3>Unknown Site</h3>;
    }
};

const DynamicPage = (props: Props): JSX.Element => {
    useCookiebot();
    useUrlHandler();

    /**
     * Note: Make sure to Add Error messages to the newly added site (In Umbraco).
     */
    const errors = useMemo(() => {
        return props.siteSettings.marketSettings
            .map(({ errorContent }) => errorContent.children)
            .reduce((acc, errorInfo) => [...acc, ...errorInfo], []);
    }, [props.siteSettings]);

    return <ErrorHandlingProvider errors={errors}>{getSiteComponents(props)}</ErrorHandlingProvider>;
};

export const getServerSideProps: GetServerSideProps = async ({ params, query, req, res }): Promise<GetServerSidePropsResult<Props>> => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const appInsights = require('applicationinsights');
    appInsights.setup(getInstrumentationKey());
    appInsights.start();
    const client = appInsights.defaultClient;

    const { currentVehicle, carFamily, allConfigurations, errorMessage, page, siteSettings } = await familyAndConfigurationsBuilder(params, query);

    console.log('getServerSideProps ran at:' + new Date().toLocaleTimeString('da-DK') + ' ', query);

    if (errorMessage) {
        client.trackException({ exception: new Error(errorMessage) });
        return {
            notFound: true,
        };
    }

    if (currentVehicle.discontinued) {
        return handleDiscontinuedVehicles(currentVehicle, carFamily);
    }

    const protocol = getProtocol(req.headers.host?.toString() ?? '');
    const canonical = `${protocol}${req.headers.host}${currentVehicle.canonicalUrl}`;

    const colorSentFromQuery = query.color as string;
    const purchaseTypeFromQuery = query.purchaseType as hesselViewModels.OwnershipTab;
    const isCompanyCar = query?.company === 'true';
    res.setHeader('Cache-Tag', currentVehicle.entityId);
    return {
        props: {
            page: { ...page, canonical },
            siteSettings,
            carFamily: carFamily,
            allConfigurations: allConfigurations[0],
            initialProductData: {
                product: currentVehicle,
                color: getCheapestCarSsrColor(currentVehicle, colorSentFromQuery),
                queryColor: getCarSsrQueryColor(currentVehicle, colorSentFromQuery),
                queryPurchaseType: purchaseTypeFromQuery || null,
                isCompanyCar,
            },
        },
    };
};

export default DynamicPage;
